import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import Breadcrumb from '~/components/Breadcrumbs/Breadcrumb';
import Toggle from '~/components/Toggle';
import TipTapEditor from '~/components/tiptap/TipTapEditor';
import {
    CampaignMessage as CampaignMessageRecord,
    CampaignMessagePayload,
    CampaignStatusOption,
    fetchCampaignMessage,
    saveCampaignMessage,
} from '~/services/campaignMessage';

// The announcement bar at the top of the client dashboard (HHLTB-2818). It
// used to be a hardcoded Fund Requests card, so changing it took a deploy.

const labelClass = 'mb-2 block text-sm font-medium text-black dark:text-white';
const inputClass =
    'w-full rounded border border-stroke bg-gray px-4 py-3 text-black focus:border-primary focus-visible:outline-none dark:border-strokedark dark:bg-meta-4 dark:text-white dark:focus:border-primary';

// The client dashboard's message color (getDashColor('message')).
const BAR_COLOR = '#952e46';

const EMPTY_FORM: CampaignMessagePayload = {
    headline: '',
    body: '',
    is_active: false,
    start_date: null,
    end_date: null,
    status_codes: [],
};

const toForm = (message: CampaignMessageRecord): CampaignMessagePayload => ({
    headline: message.headline,
    body: message.body,
    is_active: message.is_active,
    start_date: message.start_date,
    end_date: message.end_date,
    status_codes: message.status_codes,
});

// The API reads the dates in HHL's timezone, so the status line does too.
const todayInHHLTime = () =>
    new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date());

const formatDay = (day: string) => {
    const [y, m, d] = day.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
};

// What a client would see right now, as long as the saved form matches.
const describeSchedule = (form: CampaignMessagePayload) => {
    if (!form.is_active) return 'Off. Clients do not see a message.';
    const today = todayInHHLTime();
    if (form.start_date && today < form.start_date) {
        return `Scheduled. Starts showing on ${formatDay(form.start_date)}.`;
    }
    if (form.end_date && today > form.end_date) {
        return `Ended on ${formatDay(form.end_date)}. Clients do not see it.`;
    }
    return form.end_date
        ? `Showing now, through ${formatDay(form.end_date)}.`
        : 'Showing now, until it is turned off.';
};

const CampaignMessage: React.FC = () => {
    const [form, setForm] = useState<CampaignMessagePayload>(EMPTY_FORM);
    const [saved, setSaved] = useState<CampaignMessageRecord | null>(null);
    const [statusOptions, setStatusOptions] = useState<CampaignStatusOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchCampaignMessage()
            .then(resp => {
                setSaved(resp.message);
                const loaded = toForm(resp.message);
                // A message with no statuses reaches nobody, so start from
                // every status rather than an empty list.
                if (loaded.status_codes.length === 0) {
                    loaded.status_codes = resp.status_options.map(o => o.code);
                }
                setForm(loaded);
                setStatusOptions(resp.status_options);
            })
            .catch(err => {
                console.error('Error loading the campaign message:', err);
                setLoadError(err?.message || 'Could not load the campaign message');
            })
            .finally(() => setLoading(false));
    }, []);

    const setValue = <K extends keyof CampaignMessagePayload>(
        key: K,
        value: CampaignMessagePayload[K],
    ) => setForm(prev => ({ ...prev, [key]: value }));

    const toggleStatus = (code: string) =>
        setValue(
            'status_codes',
            form.status_codes.includes(code)
                ? form.status_codes.filter(c => c !== code)
                : [...form.status_codes, code],
        );

    const allSelected =
        statusOptions.length > 0 &&
        statusOptions.every(option => form.status_codes.includes(option.code));

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        toast.loading('Saving...');
        try {
            const resp = await saveCampaignMessage(form);
            toast.dismiss();
            setSaved(resp.message);
            setForm(toForm(resp.message));
            toast.success('Campaign message saved.');
        } catch (err: any) {
            toast.dismiss();
            console.error('Error saving the campaign message:', err);
            toast.error(err?.message || 'Could not save the campaign message');
        } finally {
            setSaving(false);
        }
    };

    if (loading || loadError) {
        return (
            <>
                <Breadcrumb pageName='Campaign Message' />
                <div className='rounded-sm border border-stroke bg-white p-7 shadow-default dark:border-strokedark dark:bg-boxdark'>
                    {loading ? 'Loading...' : loadError}
                </div>
            </>
        );
    }

    return (
        <>
            <Breadcrumb pageName='Campaign Message' />

            <form
                onSubmit={onSubmit}
                className='rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark'
            >
                <div className='border-b border-stroke px-7 py-4 dark:border-strokedark'>
                    <h3 className='font-medium text-black dark:text-white'>
                        Announcement bar
                    </h3>
                    <p className='mt-1 text-sm text-gray-600 dark:text-gray-400'>
                        Shown at the top of the client dashboard. Clients can
                        dismiss it, and it comes back for everyone when you save
                        a change.
                    </p>
                </div>

                <div className='flex flex-col gap-6 p-7'>
                    <div>
                        <div className='flex items-center gap-3'>
                            <Toggle
                                isOn={form.is_active}
                                setIsOn={(isOn: boolean) => setValue('is_active', isOn)}
                            />
                            <span className='font-medium text-black dark:text-white'>
                                {form.is_active ? 'On' : 'Off'}
                            </span>
                        </div>
                        <p className='mt-2 text-sm text-gray-600 dark:text-gray-400'>
                            {describeSchedule(form)}
                        </p>
                    </div>

                    <div>
                        <label htmlFor='campaign-message-headline' className={labelClass}>
                            Headline
                        </label>
                        <input
                            id='campaign-message-headline'
                            type='text'
                            maxLength={255}
                            value={form.headline}
                            onChange={e => setValue('headline', e.target.value)}
                            placeholder='NEW: Submit Fund Requests Online!'
                            className={inputClass}
                        />
                    </div>

                    <div>
                        <label className={labelClass}>Message</label>
                        <TipTapEditor
                            initialValue={form.body}
                            onChange={(html: string) => setValue('body', html)}
                        />
                    </div>

                    <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                        <div>
                            <label htmlFor='campaign-message-start' className={labelClass}>
                                Start date
                            </label>
                            <input
                                id='campaign-message-start'
                                type='date'
                                value={form.start_date || ''}
                                onChange={e => setValue('start_date', e.target.value || null)}
                                className={inputClass}
                            />
                        </div>
                        <div>
                            <label htmlFor='campaign-message-end' className={labelClass}>
                                End date
                            </label>
                            <input
                                id='campaign-message-end'
                                type='date'
                                value={form.end_date || ''}
                                min={form.start_date || undefined}
                                onChange={e => setValue('end_date', e.target.value || null)}
                                className={inputClass}
                            />
                        </div>
                        <p className='text-sm text-gray-600 dark:text-gray-400 md:col-span-2'>
                            Both dates are included. Leave them blank to show the
                            message for as long as it is on.
                        </p>
                    </div>

                    <div>
                        <div className='mb-2 flex items-center justify-between'>
                            <span className='text-sm font-medium text-black dark:text-white'>
                                Show to campaigns that are
                            </span>
                            <button
                                type='button'
                                onClick={() =>
                                    setValue(
                                        'status_codes',
                                        allSelected ? [] : statusOptions.map(o => o.code),
                                    )
                                }
                                className='text-sm text-primary underline'
                            >
                                {allSelected ? 'Clear all' : 'Select all'}
                            </button>
                        </div>
                        <div className='grid grid-cols-2 gap-2 md:grid-cols-4'>
                            {statusOptions.map(option => (
                                <label
                                    key={option.code}
                                    className='flex cursor-pointer items-center gap-2 text-sm'
                                >
                                    <input
                                        type='checkbox'
                                        checked={form.status_codes.includes(option.code)}
                                        onChange={() => toggleStatus(option.code)}
                                        className='rounded border-gray-300 text-primary focus:ring-primary'
                                    />
                                    <span className='text-gray-700 dark:text-gray-300'>
                                        {option.name}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div>
                        <span className={labelClass}>Preview</span>
                        <div
                            className='rounded px-6 py-5 text-white'
                            style={{ backgroundColor: BAR_COLOR }}
                        >
                            {form.headline && (
                                <div className='mb-3 text-sm font-bold uppercase tracking-wide'>
                                    {form.headline}
                                </div>
                            )}
                            {/* The editor's own output, shown back to the person writing it. */}
                            <div
                                className='[&_a]:font-bold [&_a]:underline'
                                dangerouslySetInnerHTML={{ __html: form.body }}
                            />
                        </div>
                    </div>
                </div>

                <div className='flex flex-wrap items-center justify-between gap-4 border-t border-stroke px-7 py-4 dark:border-strokedark'>
                    <span className='text-sm text-gray-600 dark:text-gray-400'>
                        {saved?.updated_by_name
                            ? `Last saved by ${saved.updated_by_name} on ${new Date(
                                  saved.updated_at,
                              ).toLocaleString('en-US')}`
                            : ''}
                    </span>
                    <button
                        type='submit'
                        disabled={saving}
                        className='rounded-full border-[2px] border-warning bg-warning px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:border-[#e6f7f8] hover:bg-[#e6f7f8] disabled:opacity-50'
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </form>
        </>
    );
};

export default CampaignMessage;
